// WhyChoose.jsx
import React, { useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { jarallax } from "jarallax";
import "jarallax/dist/jarallax.min.css";
import { useInView, useAnimation } from "framer-motion";

const fadeIn = {
  hidden: { opacity: 0, y: 60 },
  show: (i) => ({ 
    opacity: 1, 
    y: 0, 
    transition: { 
      duration: 0.6, 
      ease: "easeOut",
      delay: i * 0.2 
    } 
  }),
};

const fadeInMobile = {
  hidden: { opacity: 0, y: 500, scale: 0.9 },
  show: (i) => ({ 
    opacity: 1, 
    y: 0, 
    scale: 1,
    transition: { 
      duration: 0.7, 
      ease: [0.25, 0.46, 0.45, 0.94],
      delay: i * 0.15 
    } 
  }),
};

const fadeInLeft = {
  hidden: { opacity: 0, x: -120 },
  show: { opacity: 1, x: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

const stagger = { show: { transition: { staggerChildren: 0.20, delayChildren: 0.30 } } };

export default function WhyChoose() {
  const desktopRef = useRef(null);
  const mobileRef = useRef(null);
  const jarallaxRef = useRef(null);
  const desktopInView = useInView(desktopRef, { once: true, margin: "-100px" });
  const mobileInView = useInView(mobileRef, { once: true, margin: "-100px" });
  const desktopControls = useAnimation();
  const mobileControls = useAnimation();

  useEffect(() => {
    if (desktopInView) desktopControls.start("show");
  }, [desktopInView, desktopControls]);

  useEffect(() => {
    if (mobileInView) mobileControls.start("show");
  }, [mobileInView, mobileControls]);

  useEffect(() => {
    // Only init Jarallax on ACTUAL desktop - not on mobile with forced desktop layout
    const checkIfRealDesktop = () => {
      if (typeof window === "undefined") return false;
      // Check if it's a touch device (mobile/tablet)
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const isLargeScreen = window.innerWidth >= 1024;
      // Only enable parallax if large screen AND not touch device
      return isLargeScreen && !isTouchDevice;
    };
    
    const isDesktop = checkIfRealDesktop();
    
    if (isDesktop && jarallaxRef.current) {
      jarallax(jarallaxRef.current, {
        speed: 0.5,
        type: "scroll",
        imgSrc: "./images/Why3.jpg",
        imgSize: "cover",
        imgPosition: "50% 50%",
      });
    }
    return () => {
      if (jarallaxRef.current) {
        try { jarallax(jarallaxRef.current, "destroy"); } catch {}
      }
    };
  }, []);

  // Shared data for blocks
  const blocks = [
    {
      n: "01",
      pages: [],
      lines: [
        "COMMUNICATION",
        "For Real estate projects involve multiple disciplines—strong coordination avoids delays, rework, and cost escalations. To ensures clarity in design intent across all project stages, helps prevent miscommunication and design conflicts. Allign all stakeholders: developers, architects, consultants, contractors, and clients",
      ],
    },
    {
      n: "02",
      pages: [],
      lines: [
        "DOCUMENTATION",
        "We belief beautiful design is only valuable if it is buildable, affordable, and meets the target market’s need. To Link design decisions with business objectives (budget, timeline, market positioning) and Encourage value engineering without compromising quality to Ensure consistency across architectural, structural, MEP, and interior drawings",
      ],
    },
    {
      n: "03",
      pages: [],
      lines: [
        "EXECUTION ALIGNMENT",
        "We aim at Proper documentation to avoid on-site confusion and ensure construction aligns with approved designs. Establishes a structured design timeline with milestones (concept, DD, GFC, etc.) To Manages design revisions, approvals, and version tracking, Supports smooth handover for execution and future maintenance",
      ],
    },
  ];

  return (
    <section className="relative w-full bg-canvas-300 text-neutral-900 overflow-hidden">
      {/* Desktop/Large screens: original split layout with jarallax */}
      <div className="absolute inset-0 grid grid-cols-2 hidden lg:grid">
        <motion.div
          ref={jarallaxRef}
          className="jarallax relative col-span-1"
          variants={fadeInLeft}
          initial="hidden"
          animate={desktopControls}
          data-jarallax
          data-speed="0.5"
          style={{
            backgroundImage: 'url(./images/Why3.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: '50% 50%',
            backgroundAttachment: 'scroll'
          }}
        />
        <div className="col-span-1 bg-canvas-300" />
      </div>

      <div className="relative z-10 mx-auto flex max-w-6xl  md:px-10 md:py-16">
        {/* Desktop content column (right side) */}
        <motion.div
          ref={desktopRef}
          variants={stagger}
          initial="hidden"
          animate={desktopControls}
          className="ml-auto hidden lg:flex w-full flex-col justify-center md:w-1/2 gap-0"
        >
          <motion.h1
            variants={fadeIn}
            className="tracking-tight pl-10 pt-6 text-neutral-900 drop-shadow-md"
            style={{ fontWeight: 600, letterSpacing: "0.02em", fontSize: "56px", lineHeight: "1" }}
          >
            Why Choose Us
          </motion.h1>

          <div className="mt-10 space-y-16">
            {blocks.map((block, index) => (
              <motion.div key={block.n} variants={fadeIn} custom={index} className="relative flex items-start gap-6">
                {/* Big number and halo only on desktop */}
                <div className="pointer-events-none absolute -left-[50%] top-0 select-none z-10">
                  <span className="text-[128px] leading-[128px] text-neutral-200 font-light drop-shadow-lg">{block.n}</span>
                </div>
                <div className="absolute -left-16 top-4 h-32 w-32 rounded-full bg-canvas-300 z-10" />

                <div className="relative pl-10 z-20 mt-5 grow">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <p className="text-[18px] uppercase tracking-wide text-neutral-600 drop-shadow font-bold">{block.lines[0]}</p>
                      {block.lines.slice(1).map((t) => (
                        <p
                          key={t}
                          className={`${/W\.A\.Ve|COMMUNICATION|DOCUMENTATION|EXECUTION ALLIGNMENT|Viaggio/.test(t) ? "text-[18px] font-semibold leading-tight drop-shadow" : "text-[14px] text-neutral-500 drop-shadow-sm"}`}
                        >
                          {t}
                        </p>
                      ))}
                    </div>
                    <div className="pl-8">
                      {block.pages.map((p) => (
                        <p key={p} className="text-[18px] text-neutral-700 drop-shadow-sm">{p}</p>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Mobile/Tablet: background image + white rounded card with content */}
        <div
          className="lg:hidden relative w-full m-0"
          style={{
            backgroundImage: "url('./images/Why3.jpg')",
            backgroundSize: "cover",
            backgroundPosition: "50% 50%",
          }}
        >
          
          <div className="py-12 px-4">
            <motion.div
              ref={mobileRef}
              variants={stagger}
              initial="hidden"
              animate={mobileControls}
              className="mx-auto w-full bg-white/60 backdrop-blur-md rounded-2xl shadow-xl p-6 border border-white/20"
            >
              <motion.h2
                variants={fadeIn}
                className="text-neutral-900 color-primary"
                style={{ fontWeight: 600, letterSpacing: "0.02em", fontSize: "34px", lineHeight: "1.1" }}
              >
                Why Choose Us
              </motion.h2>

              <div className="mt-6 space-y-8">
                {blocks.map((block, index) => (
                  <motion.div 
                    key={block.n} 
                    variants={fadeInMobile} 
                    className="relative"
                    custom={index}
                  >
                    {/* Mobile card with enhanced visibility */}
                    <div className="space-y-2 mx-auto w-full bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl p-6 border border-white/30 hover:shadow-3xl transition-all duration-300">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-2xl font-bold  bg-white-50 rounded-full w-10 h-10 flex items-center justify-center"
                          style={{ color: "#C3A585" }}>
                          {block.n}
                        </span>
                        <p className="text-[14px] uppercase tracking-wide text-neutral-700 font-semibold">{block.lines[0]}</p>
                      </div>
                      {block.lines.slice(1).map((t) => (
                        <p
                          key={t}
                          className={`${/W\.A\.Ve|COMMUNICATION|DOCUMENTATION|EXECUTION ALLIGNMENT|Viaggio/.test(t) ? "text-[16px] font-semibold leading-snug text-neutral-800" : "text-[14px] text-neutral-700 leading-relaxed"}`}
                        >
                          {t}
                        </p>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
